import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { coerceSchema, renderSubmissionHtml } from '../lib/hostedForm';
import { formatDateBritish, todayIso } from '../lib/compliance';
import FusionLogo from '../components/FusionLogo';
import SupplierFooter from '../components/SupplierFooter';
import SignaturePad from '../components/compliance/SignaturePad';
import HostedFormSignPage from './HostedFormSignPage';

// The supplier portal's own chrome, minus everything that needs an account.
//
// The delegate has never seen Supply Pilot and has no session, so the header
// carries the brand and nothing else — no account menu, no navigation, nothing
// that would 404 or prompt a login. The footer is the portal's real one, which
// is worth keeping: it carries the support contact, and a stranger asked to
// sign something for a company they may not work for is exactly who needs a
// way to check it's genuine.
function DelegateShell({ children }) {
  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <div className="bg-surface-sidebar border-b border-white/[0.08]">
        <div className="max-w-5xl mx-auto px-4 sm:px-10 h-[76px] sm:h-[90px] flex items-center gap-2.5">
          <FusionLogo icon alt="Fusion" className="h-[38px] w-auto object-contain" />
          <span className="font-['Metropolis'] text-[19px] font-extrabold tracking-[0.14em] text-white leading-none">FUSION</span>
          <div className="hidden sm:block w-px h-9 bg-white/15 flex-shrink-0 mx-3.5" />
          <span className="hidden sm:inline-flex items-center whitespace-nowrap font-['Metropolis'] text-[12px] font-bold tracking-[0.16em] uppercase text-sb-text">
            Supply Pilot <span className="text-sb-muted font-semibold">&nbsp;·&nbsp; Partner Portal</span>
          </span>
        </div>
      </div>
      <div className="flex-1">{children}</div>
      {/* No help slide-out here — it needs a session. The footer renders its
          contact details without it. */}
      <SupplierFooter containerClassName="max-w-5xl px-8" />
    </div>
  );
}

// The delegate's view of a single document, reached at /d/<token>.
//
// PUBLIC BY DESIGN and deliberately session-less. The delegate is someone at
// the supplier who was asked to complete or sign one document — a director, a
// safety officer — not a portal user. Everything they can see or do goes
// through two service-role endpoints that re-check the token, so they hold no
// database access of their own and nothing here depends on RLS.
//
// This renders the SAME signer the supplier uses. That's the point: a
// delegated document must not be a second, quietly-diverging implementation of
// reading and signing, or the sectioned reading gate would apply to one and
// not the other.

// Acknowledge documents: read the copy, sign it, done. Mirrors what the
// supplier sees in ComplianceAcknowledgeModal — the same three gates (read it,
// tick it, sign it) — but standalone, because a delegate has no session and the
// modal is built around one.
function DelegateAcknowledge({ payload, onDone }) {
  const doc = payload.document;
  const [agreed, setAgreed] = useState(false);
  const [signerName, setSignerName] = useState(payload.delegation.inviteeName || '');
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const canSign = agreed && signerName.trim().length > 1 && !!signature;
  const blocking = !agreed ? 'tick that you have read it'
    : signerName.trim().length <= 1 ? 'your full name'
      : !signature ? 'your signature' : null;

  const submit = async () => {
    if (!canSign) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch('/api/delegation-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: payload.token,
          signerName: signerName.trim(),
          signatureImage: signature,
          contentHash: doc.contentHash || null,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || 'Could not submit. Please try again.');
      onDone(body.warning);
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[860px] mx-auto px-6 py-8 font-['Metropolis']">
      <div className="bg-[#15170F] text-white rounded-t-2xl px-5 py-3 text-[12px]">
        <strong className="font-semibold">{payload.delegation.invitedBy || 'A colleague'}</strong>
        {' '}at <strong className="font-semibold">{payload.supplier?.supplier_name}</strong>
        {' '}asked you to read and sign this document.
      </div>

      <div className="bg-white border border-line border-t-0 rounded-b-2xl px-7 py-7">
        <h1 className="text-2xl font-extrabold text-ink leading-tight">{doc.label}</h1>
        {doc.versionValue ? <p className="text-[12px] text-[#51534A] mt-1">Version {doc.versionValue}</p> : null}

        {/* The copy. Richtext when we hold it, otherwise the uploaded file. */}
        <div className="mt-6">
          {doc.contentRichtext ? (
            <div className="prose-compliance text-[15px] text-[#2A2E24] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: doc.contentRichtext }} />
          ) : doc.contentFileUrl ? (
            <iframe
              title={doc.label}
              src={doc.contentFileUrl}
              className="w-full rounded-lg border border-line"
              style={{ height: '60vh' }} />
          ) : (
            // Never a silent empty page: if there's nothing to read there is
            // nothing to agree to, and signing would be meaningless.
            <p className="text-[13px] text-[#B01F06]">
              This document has no content to read. Please tell {payload.delegation.invitedBy || 'the person who sent it'} —
              don't sign it.
            </p>
          )}
        </div>

        {(doc.contentRichtext || doc.contentFileUrl) && (
          <div className="mt-8 pt-6 border-t border-dashed border-line">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={agreed} disabled={busy}
                onChange={e => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-active-green flex-shrink-0" />
              <span className="text-[14px] text-ink">I have read this document and agree to it on behalf of {payload.supplier?.supplier_name}.</span>
            </label>

            <div className="mt-5 max-w-[420px]">
              <label htmlFor="del-ack-name" className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-1.5">Full name</label>
              <input id="del-ack-name" value={signerName} onChange={e => setSignerName(e.target.value)} disabled={busy}
                placeholder="Your full name"
                className="w-full bg-white border border-line rounded-lg px-3 py-2 text-[15px] text-ink focus:outline-none focus:border-[#2F6E14]" />
            </div>

            <div className="mt-4 max-w-[560px]">
              <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-1.5">Signature</span>
              <SignaturePad onChange={setSignature} disabled={busy} />
            </div>

            {err && <p role="alert" className="text-[13px] text-[#B01F06] mt-4">{err}</p>}

            <div className="flex items-center gap-4 mt-6 flex-wrap">
              <button type="button" onClick={submit} disabled={!canSign || busy}
                className="rounded-xl bg-[#92D400] text-[#131313] text-sm font-bold px-6 py-2.5 transition-colors hover:bg-[#a4e810] disabled:opacity-45 disabled:cursor-not-allowed">
                {busy ? 'Signing…' : 'Sign & submit'}
              </button>
              {/* Names what's missing rather than sitting there disabled. */}
              <p role="status" aria-live="polite" className="text-[12px] text-[#51534A]">
                {blocking ? <>Before you can sign: <span className="font-semibold text-[#7A4E10]">{blocking}</span>.</>
                  : <span className="text-fusion-forest font-semibold">Ready to sign.</span>}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DelegatedDocumentPage() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | done | error
  const [payload, setPayload] = useState(null);
  const [message, setMessage] = useState(null);
  const [detail, setDetail] = useState(null);
  const [returnsToInviter, setReturnsToInviter] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/delegation-lookup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const body = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) {
          setMessage(body.error || 'This link could not be opened.');
          // Kept so the page can say WHY. Three rounds of "still not working"
          // went by with the real reason only ever in a server log.
          setDetail([body.step, body.detail].filter(Boolean).join(' — ') || null);
          setState('error');
          return;
        }
        // Carry the token on the payload — the acknowledge branch submits with
        // it and shouldn't have to reach back into the route params.
        setPayload({ ...body, token });
        setState('ready');
      } catch {
        if (!cancelled) { setMessage('Could not reach the server. Check your connection and try again.'); setState('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const schema = useMemo(() => coerceSchema(payload?.document?.formSchema), [payload]);
  const signsNow = payload?.delegation?.scope === 'complete_and_sign';

  // The signer hands back everything it collected instead of writing to
  // Supabase itself — a delegate has no session, so the write has to happen
  // server-side against the token.
  const handleDelegatedSubmit = useCallback(async ({ values, ticks, signerName, signatureImage, reading }) => {
    const todayLabel = formatDateBritish(todayIso());
    const documentHtml = signsNow
      ? renderSubmissionHtml(schema, {
        fields: values, ticks, signerName: (signerName || '').trim(), signedAtLabel: todayLabel,
        docLabel: payload.document.label, version: payload.document.versionValue, signatureImage,
      })
      : null;

    const r = await fetch('/api/delegation-submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        responses: { fields: values, clauses: ticks, reading },
        signerName, signatureImage: null, documentHtml,
        contentHash: payload.document.contentHash || null,
      }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || 'Could not submit. Please try again.');
    setReturnsToInviter(!!body.returnsToInviter);
    setMessage(body.warning || null);
    setState('done');
  }, [token, schema, payload, signsNow]);

  if (state === 'loading') {
    return (
      <DelegateShell>
        <div className="flex items-center justify-center py-24">
          <p className="font-['Metropolis'] text-[#51534A] tracking-[3px] uppercase text-sm">Loading…</p>
        </div>
      </DelegateShell>
    );
  }

  if (state === 'error') {
    return (
      <DelegateShell>
        <div className="flex items-start justify-center px-6 py-16 font-['Metropolis']">
          <div className="w-full max-w-[520px] bg-white border border-line rounded-2xl px-7 py-8">
            <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#B01F06]">Can't open this</p>
            <h1 className="text-xl font-bold text-ink mt-2 leading-tight">{message}</h1>
            <p className="text-sm text-[#51534A] mt-3 leading-relaxed">
              Links are for one document and expire after 14 days. If you still need to complete
              this, the person who sent it can send a new one.
            </p>
            {detail && (
              <details className="mt-5">
                <summary className="text-xs font-semibold text-[#51534A] cursor-pointer hover:text-fusion-forest">
                  Technical detail (useful when reporting this)
                </summary>
                <p className="font-mono text-[11px] text-[#7A1B05] mt-2 break-words">{detail}</p>
              </details>
            )}
          </div>
        </div>
      </DelegateShell>
    );
  }

  if (state === 'done') {
    return (
      <DelegateShell>
        <div className="flex items-start justify-center px-6 py-16 font-['Metropolis']">
          <div className="w-full max-w-[520px] bg-white border border-line rounded-2xl px-7 py-8 text-center">
            <h1 className="text-xl font-bold text-ink leading-tight">
              {returnsToInviter ? 'Thanks — sent back for signing' : 'Thanks — that\'s done'}
            </h1>
            <p className="text-sm text-[#51534A] mt-3 leading-relaxed">
              {returnsToInviter
                ? `Your answers have gone back to ${payload?.delegation?.invitedBy || 'the person who asked you'} to sign off. You don't need to do anything else.`
                : `${payload?.document?.label} has been submitted to Fusion 2K for review. You don't need to do anything else.`}
            </p>
            {message && <p className="text-[13px] text-[#7A4E10] mt-4">{message}</p>}
          </div>
        </div>
      </DelegateShell>
    );
  }

  // An ACKNOWLEDGE document is not a hosted form: it has no form_schema, its
  // copy is richtext or an uploaded file, and it records in
  // document_acknowledgements. Routing it through the form renderer gave a
  // cover page, no copy at all, and a Sign button that could never be
  // satisfied because there was no signature block to put a pad in.
  if (payload.document.satisfiedBy === 'acknowledge') {
    return (
      <DelegateShell>
        <DelegateAcknowledge payload={payload} onDone={(warning) => { setMessage(warning || null); setReturnsToInviter(false); setState('done'); }} />
      </DelegateShell>
    );
  }

  return (
    <>
      {/* Who asked, and what they asked for. A delegate arrives with no
          context at all — they clicked a link in an email from a colleague —
          so the page has to say whose document this is and whether they're
          expected to sign it or just fill it in. */}
      <div className="fixed top-0 left-0 right-0 z-[80] bg-[#15170F] text-white px-5 py-2">
        <div className="max-w-[1180px] mx-auto font-['Metropolis'] text-[12px] flex items-center justify-between gap-4 flex-wrap">
          <span>
            <strong className="font-semibold">{payload.delegation.invitedBy || 'A colleague'}</strong>
            {' '}at{' '}
            <strong className="font-semibold">{payload.supplier?.supplier_name}</strong>
            {' '}asked you to {signsNow ? 'complete and sign' : 'fill in'} this document.
          </span>
          {!signsNow && (
            <span className="text-white/70">You won't be signing it — it goes back to them for that.</span>
          )}
        </div>
      </div>
      <div className="pt-9">
        <HostedFormSignPage
          document={payload.document}
          supplier={payload.supplier}
          supplierId={payload.supplier?.id}
          signer={{ name: payload.delegation.inviteeName, email: payload.delegation.inviteeEmail }}
          delegated={{
            scope: payload.delegation.scope,
            invitedBy: payload.delegation.invitedBy,
            onSubmit: handleDelegatedSubmit,
          }}
          onSuccess={() => {}}
          onClose={() => { window.location.href = '/'; }}
        />
      </div>
    </>
  );
}
